
import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import { X, Briefcase, Save, AlertCircle } from "lucide-react";

const API_URL = "https://ims.holdthechild.net/api";

const experienceOptions = [
  { value: "Entry Level (0–1 years)", label: "Entry Level (0–1 years)" },
  { value: "Junior (1–3 years)", label: "Junior (1–3 years)" },
  { value: "Mid-Level (3–5 years)", label: "Mid-Level (3–5 years)" },
  { value: "Senior (5–8 years)", label: "Senior (5–8 years)" },
  { value: "Lead / Manager (8+ years)", label: "Lead / Manager (8+ years)" }
];

const educationOptions = [
  { value: "High School", label: "High School" },
  { value: "Diploma", label: "Diploma" },
  { value: "Bachelor’s Degree", label: "Bachelor’s Degree" },
  { value: "Postgraduate Diploma", label: "Postgraduate Diploma" },
  { value: "Master’s Degree", label: "Master’s Degree" },
  { value: "PhD", label: "PhD" },
  { value: "Professional Certification", label: "Professional Certification" }
];


const AddApplicantForm = ({ onClose, onSuccess, applicantToEdit }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    jobId: "",
    skills: "",
    experience: "",
    education: "",
    source: "",
    status: "Applied",
    appliedOn: new Date().toISOString().split("T")[0], // default to today
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const modalRef = useRef(null);
  const token = localStorage.getItem("token");

  const sourceOptions = [
    { value: "Email", label: "Email" },
    { value: "Referral", label: "Referral" },
    { value: "Walk-in", label: "Walk-in" },
    { value: "LinkedIn", label: "LinkedIn" },
    { value: "Job Portal", label: "Job Portal" },
  ];

  const statusOptions = [
    { value: "Applied", label: "Applied" },
    { value: "Pending", label: "Pending" },
    { value: "Shortlisted", label: "Shortlisted" },
    { value: "Successful", label: "Successful" },
    { value: "Unsuccessful", label: "Unsuccessful" },
  ];

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_URL}/job-posts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Jobs fetched:", res.data.data);
        
        // Adjust based on API response structure
        setJobs(res.data.data || []);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setJobs([]); // fallback to empty array
      }
    };
    fetchJobs();
  }, []);

  // Pre-fill when editing
  useEffect(() => {
    if (applicantToEdit) {
      setFormData({
        fullName: applicantToEdit.fullName || "",
        email: applicantToEdit.email || "",
        phone: applicantToEdit.phone || "",
        jobId: applicantToEdit.jobId?._id || "",
        skills: applicantToEdit.skills || "",
        experience: applicantToEdit.experience || "",
        education: applicantToEdit.education || "",
        source: applicantToEdit.source || "",
        status: applicantToEdit.status || "Applied",
        appliedOn: applicantToEdit.appliedOn ? 
          new Date(applicantToEdit.appliedOn).toISOString().split("T")[0] : 
          new Date().toISOString().split("T")[0]
      });
    }
  }, [applicantToEdit]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Use the correct backend URL
      const endpoint = applicantToEdit
        ? `${API_URL}/applicants/${applicantToEdit._id}`
        : `${API_URL}/applicants`;

      const method = applicantToEdit ? "put" : "post";
      const payload = new FormData();
      Object.entries(formData).forEach(([k, v]) => payload.append(k, v));
      if (resumeFile) payload.append("document", resumeFile);

      // Create axios config with headers
      const config = {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` // Add the authorization header
        }
      };

      console.log("Submitting to:", endpoint);
      console.log("Form data:", formData);

      const res = await axios[method](endpoint, formData, config);

      console.log("Response:", res.data);
      toast.success(`Applicant ${applicantToEdit ? "updated" : "added"} successfully`);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Submit error:", err);
      
      // Get more detailed error information
      let errorMessage = "Failed to submit applicant";
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error("Error data:", err.response.data);
        console.error("Error status:", err.response.status);
        console.error("Error headers:", err.response.headers);
        
        if (err.response.status === 404) {
          errorMessage = "API endpoint not found. Please check the server URL.";
        } else if (err.response.status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (err.response.status === 403) {
          errorMessage = "You don't have permission to perform this action.";
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.request) {
        // The request was made but no response was received
        console.error("Error request:", err.request);
        errorMessage = "No response from server. Please check your connection.";
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Error message:", err.message);
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close modal on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const jobOptions = jobs.map((job) => ({ value: job._id, label: job.vacancy }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-100">
          <div className="flex items-center justify-between px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">
                  {applicantToEdit ? "Edit Applicant" : "Add New Applicant"}
                </h2>
                <p className="text-lg text-black-100 mt-1">Recruitment Management</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow px-8 py-6 overflow-y-auto">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">
                  <p className="font-medium">Error</p>
                  <p className="mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full px-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                placeholder="Full Name"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                placeholder="Email Address"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                placeholder="Phone Number"
                required
              />
            </div>

            {/* Job Applied */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Job Applied <span className="text-red-500">*</span>
              </label>
              <Select
                options={jobOptions}
                value={jobOptions.find((option) => option.value === formData.jobId)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, jobId: selected?.value || "" }))}
                placeholder="Select Job..."
                className="rounded-xl"
              />
            </div>

            {/* Skills */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Skills <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="skills"
                value={formData.skills}
                onChange={handleChange}
                className="w-full px-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                placeholder="Skills"
                required
              />
            </div>
            {/* Experience */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Experience <span className="text-red-500">*</span>
              </label>
              <Select
                options={experienceOptions}
                value={experienceOptions.find((option) => option.value === formData.experience)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, experience: selected?.value || "" }))}
                placeholder="Select Experience..."
                className="rounded-xl"
              />
            </div>
            {/* Education */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Education <span className="text-red-500">*</span>
              </label>
              <Select
                options={educationOptions}
                value={educationOptions.find((option) => option.value === formData.education)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, education: selected?.value || "" }))}
                placeholder="Select Education..."
                className="rounded-xl"
              />
            </div>

            {/* Source */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Source <span className="text-red-500">*</span>
              </label>
              <Select
                options={sourceOptions}
                value={sourceOptions.find((option) => option.value === formData.source)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, source: selected?.value || "" }))}
                placeholder="Select Source..."
                className="rounded-xl"
              />
            </div>

            {/* Status */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Status <span className="text-red-500">*</span>
              </label>
              <Select
                options={statusOptions}
                value={statusOptions.find((option) => option.value === formData.status)}
                onChange={(selected) => setFormData((prev) => ({ ...prev, status: selected?.value || "" }))}
                className="rounded-xl"
              />
            </div>
            
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-800 mb-3">
                Applied On
              </label>
              <input
                type="date"
                name="appliedOn"
                value={formData.appliedOn}
                onChange={handleChange}
                className="w-full px-3 py-3 text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
              />
            </div>
            {/* Document Upload */}
          <input type="file" accept=".pdf,.doc,.docx" onChange={e => setResumeFile(e.target.files[0])} />

            {/* Footer */}
            <div className="flex justify-end space-x-4 mt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                  loading
                    ? "bg-orange-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl"
                }`}
              >
                {loading ? "Submitting..." : <><Save className="w-5 h-5 inline mr-2" /> Submit Applicant</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddApplicantForm;
